/**
 * AgentCorp Office Sprites
 *
 * Collection of sprite components for office furniture and decorations.
 * Supports both 2D flat view and 3D dollhouse view.
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// ============================================
// 3D DOLLHOUSE WRAPPER
// ============================================

/**
 * Wrapper component that makes sprites "stand up" in dollhouse view
 * by applying counter-transforms to undo the parent's isometric rotation
 */
export const Sprite3D = ({ children, x, y, zIndex, isDollhouseView = false, className = '' }) => {
  const baseStyle = {
    left: x,
    top: y,
    zIndex: zIndex || Math.floor(y),
  };

  if (!isDollhouseView) {
    return (
      <div className={`absolute ${className}`} style={baseStyle}>
        {children}
      </div>
    );
  }

  // In dollhouse view, apply counter-transform to make sprite stand upright
  // Parent has: rotateX(55deg) rotateZ(-45deg)
  // We counter with: rotateZ(45deg) rotateX(-55deg) to stand them up
  return (
    <div
      className={`absolute ${className}`}
      style={{
        ...baseStyle,
        transformStyle: 'preserve-3d',
      }}
    >
      <div
        style={{
          transform: 'rotateZ(45deg) rotateX(-55deg)',
          transformOrigin: 'center bottom',
          filter: 'drop-shadow(4px 8px 6px rgba(0,0,0,0.4))',
        }}
      >
        {children}
      </div>
    </div>
  );
};

Sprite3D.propTypes = {
  children: PropTypes.node.isRequired,
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  zIndex: PropTypes.number,
  isDollhouseView: PropTypes.bool,
  className: PropTypes.string,
};

// ============================================
// HELPERS
// ============================================

/**
 * Get furniture metadata (dimensions and type)
 * @param {string} type - Furniture type identifier
 * @returns {Object} Metadata with kind, width, and height
 */
export const getFurnitureMeta = (type) => {
  if (type.includes('desk') && !type.includes('phone')) return { kind: 'desk', width: 82, height: 54 };
  if (type.includes('chair') || type.includes('armchair') || type.includes('stool')) return { kind: 'chair', width: 36, height: 34 };
  if (type.includes('sofa')) return { kind: 'sofa', width: 92, height: 48 };
  if (type.includes('book') || type.includes('cabinet') || type.includes('shelf')) return { kind: 'storage', width: 48, height: 72 };
  if (type.includes('server')) return { kind: 'server', width: 48, height: 84 };
  if (type.includes('coffee') || type.includes('vending') || type.includes('water') || type.includes('fridge') || type.includes('counter') || type.includes('bar-counter')) return { kind: 'refreshment', width: 38, height: 58 };
  if (type.includes('tree') || type.includes('plant') || type.includes('cactus') || type.includes('bonsai') || type.includes('flower')) return { kind: 'plant', width: 36, height: 54 };
  if (type.includes('rug') || type.includes('yoga-mat') || type.includes('skateboard')) return { kind: 'rug', width: 120, height: 78 };
  if (type.includes('window') || type.includes('poster') || type.includes('clock') || type.includes('photo') || type.includes('whiteboard') || type.includes('tv') || type.includes('dart') || type.includes('hoop') || type.includes('camera')) return { kind: 'wall', width: 48, height: 38 };
  if (type.includes('divider') || type.includes('wall') || type.includes('door') || type.includes('stairs')) return { kind: 'divider', width: 90, height: 24 };
  if (type.includes('table') || type.includes('meeting-table') || type.includes('projector') || type.includes('printer') || type.includes('monitor') || type.includes('dining')) return { kind: 'table', width: 88, height: 48 };
  if (type.includes('pet') || type.includes('cat') || type.includes('dog') || type.includes('fish') || type.includes('bunny') || type.includes('hamster') || type.includes('bird') || type.includes('turtle')) return { kind: 'pet', width: 44, height: 30 };
  if (type.includes('lamp')) return { kind: 'lamp', width: 25, height: 60 };
  if (type.includes('arcade')) return { kind: 'arcade', width: 50, height: 70 };
  if (type.includes('ping-pong')) return { kind: 'pingpong', width: 100, height: 60 };
  if (type.includes('foosball')) return { kind: 'foosball', width: 90, height: 50 };
  if (type.includes('reception')) return { kind: 'reception', width: 120, height: 50 };
  if (type.includes('bicycle') || type.includes('bike') || type.includes('gym-bike')) return { kind: 'bike', width: 50, height: 40 };
  if (type.includes('christmas') || type.includes('pumpkin') || type.includes('ghost') || type.includes('snowman') || type.includes('heart') || type.includes('fireworks') || type.includes('menorah')) return { kind: 'seasonal', width: 40, height: 50 };
  if (type.includes('pizza') || type.includes('fruit') || type.includes('cake') || type.includes('sushi')) return { kind: 'food', width: 30, height: 30 };
  if (type.includes('scooter') || type.includes('segway')) return { kind: 'vehicle', width: 35, height: 45 };
  if (type.includes('laptop') || type.includes('phone') || type.includes('tablet') || type.includes('headset') || type.includes('speaker') || type.includes('router')) return { kind: 'electronic', width: 30, height: 30 };
  return { kind: 'generic', width: 44, height: 44 };
};

const getPetEmoji = (type) => {
  if (type.includes('dog')) return '🐕';
  if (type.includes('fish')) return '🐠';
  if (type.includes('bunny')) return '🐰';
  if (type.includes('hamster')) return '🐹';
  if (type.includes('bird')) return '🦜';
  if (type.includes('turtle')) return '🐢';
  return '🐱';
};

const getPetBadge = (mood) => {
  if (mood === 'zoomies') return '⚡';
  if (mood === 'napping') return '💤';
  if (mood === 'perched') return '🎶';
  if (mood === 'bouncing') return '✨';
  if (mood === 'wandering') return '🐾';
  return '💫';
};

// ============================================
// SPRITE COMPONENTS
// ============================================

export const DeskSprite = ({ x, y, variant = 'modern', active = false, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative">
      <div
        className="absolute w-[82px] h-4 rounded-md"
        style={{
          background: variant === 'gaming'
            ? 'linear-gradient(90deg, #111827 0%, #4338ca 100%)'
            : 'linear-gradient(90deg, #7c5b3d 0%, #5b3f2a 100%)',
          boxShadow: active ? '0 0 24px rgba(34,197,94,0.35)' : '0 6px 0 rgba(62,39,20,0.7)',
        }}
      />
      <div className="absolute left-2 top-4 h-7 w-2 rounded-sm bg-[#4b2e1a]" />
      <div className="absolute right-2 top-4 h-7 w-2 rounded-sm bg-[#4b2e1a]" />
      <div className="absolute left-6 -top-6 h-6 w-8 rounded-md border border-white/10 bg-[#111827]">
        <div className={`mx-auto mt-1 h-3.5 w-6 rounded-sm ${active ? 'animate-pulse' : ''}`} style={{ backgroundColor: active ? '#4ade80' : '#38bdf8' }} />
      </div>
      <div className="absolute left-[31px] -top-[2px] h-2.5 w-2 rounded-sm bg-[#475569]" />
      <div className="absolute left-6 top-1 h-2 w-8 rounded-sm bg-[#1f2937]" />
      <div className="absolute right-3 -top-1 h-3.5 w-3 rounded-sm bg-white">
        <div className="h-2.5 w-full rounded-t-sm bg-[#92400e]" />
      </div>
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

DeskSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  variant: PropTypes.oneOf(['modern', 'gaming']),
  active: PropTypes.bool,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const ChairSprite = ({ x, y, color = '#475569', compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative">
      <div className="h-4 w-7 rounded-md" style={{ backgroundColor: color }} />
      <div className="absolute -top-4 left-1 h-5 w-5 rounded-t-md" style={{ backgroundColor: color }} />
      <div className="absolute left-3 top-4 h-3 w-1 rounded-full bg-[#334155]" />
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y) - 1} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

ChairSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  color: PropTypes.string,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const PlantSprite = ({ x, y, large = false, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative">
      <div className={`rounded-b-xl bg-[#92400e] ${large ? 'h-5 w-8' : 'h-4 w-6'}`} />
      <div className={`absolute rounded-full bg-[#22c55e] ${large ? '-left-1 -top-8 h-10 w-10' : '-left-1 -top-6 h-7 w-7'}`} />
      <div className={`absolute rounded-full bg-[#16a34a] ${large ? 'left-4 -top-9 h-8 w-8' : 'left-3 -top-6 h-5 w-5'}`} />
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

PlantSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  large: PropTypes.bool,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const SofaSprite = ({ x, y, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative h-9 w-24 rounded-2xl bg-[#a78bfa] shadow-lg">
      <div className="absolute inset-x-2 top-1 flex gap-1">
        <div className="h-4 flex-1 rounded-xl bg-white/15" />
        <div className="h-4 flex-1 rounded-xl bg-white/15" />
      </div>
      <div className="absolute -top-3 left-0 h-4 w-24 rounded-t-2xl bg-[#8b5cf6]" />
      <div className="absolute -left-1 top-1 h-6 w-3 rounded-l-xl bg-[#8b5cf6]" />
      <div className="absolute -right-1 top-1 h-6 w-3 rounded-r-xl bg-[#8b5cf6]" />
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

SofaSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const StorageSprite = ({ x, y, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative h-16 w-12 rounded-md bg-[#8b4513] shadow-md">
      {[0, 1, 2].map((row) => (
        <div key={row} className="absolute left-1 right-1 flex gap-px" style={{ top: row * 18 + 4 }}>
          {['#ef4444', '#3b82f6', '#22c55e', '#f59e0b'].map((bookColor, index) => (
            <div key={bookColor + index} className="w-2 rounded-sm" style={{ height: 10 + (index % 2) * 4, backgroundColor: bookColor }} />
          ))}
        </div>
      ))}
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

StorageSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const RefreshmentSprite = ({ x, y, type = 'coffee', compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative h-12 w-8 rounded-md bg-[#334155] shadow-lg">
      <div className="mx-auto mt-1 h-2 w-5 rounded bg-[#0f172a]" />
      <div className="mx-auto mt-2 flex w-4 justify-center rounded-sm bg-[#22c55e] text-[8px] text-black">{type.includes('water') ? 'H2O' : '☕'}</div>
      <div className="absolute bottom-1 left-2 h-3 w-4 rounded-b-sm bg-[#1e293b]" />
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

RefreshmentSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  type: PropTypes.string,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const ServerSprite = ({ x, y, active = false, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="h-20 w-10 rounded-md bg-[#111827] shadow-xl">
      {[0, 1, 2, 3, 4].map((row) => (
        <div key={row} className="mx-auto mt-1 h-3 w-8 rounded-sm bg-[#334155]">
          <div className="float-right mr-1 mt-1 flex gap-px">
            <div className={`h-1 w-1 rounded-full ${active ? 'bg-green-400 animate-pulse' : 'bg-slate-600'}`} />
            <div className="h-1 w-1 rounded-full bg-sky-400" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

ServerSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  active: PropTypes.bool,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const DividerSprite = ({ x, y, width = 90, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div
      className="rounded-full border border-white/15 bg-[#e2e8f0]/70"
      style={{ width, height: 12 }}
    />
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

DividerSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const WindowSprite = ({ x, y, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="h-10 w-16 rounded-xl border border-white/30 bg-gradient-to-b from-sky-200 to-sky-400 p-1 shadow-lg">
      <div className="grid h-full grid-cols-2 gap-1">
        <div className="rounded bg-white/25" />
        <div className="rounded bg-white/25" />
      </div>
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={6} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

WindowSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const RugSprite = ({ x, y, width = 120, height = 78, compact = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';
  // Rugs stay flat on the floor - no 3D transform needed
  return (
    <div
      className={`absolute rounded-3xl border border-white/10 bg-[#8b5cf6]/15 ${scale}`}
      style={{ left: x, top: y, width, height, zIndex: 1, boxShadow: 'inset 0 0 20px rgba(0,0,0,0.12)' }}
    >
      <div className="absolute inset-2 rounded-[20px] border border-white/10" />
    </div>
  );
};

RugSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  compact: PropTypes.bool,
};

export const PetSprite = ({ x, y, type = 'cat', mood = 'wandering', roaming = false, compact = false, isDollhouseView = false }) => {
  const [frame, setFrame] = useState(0);
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  useEffect(() => {
    if (!roaming) return undefined;
    const interval = setInterval(() => {
      setFrame((current) => (current + 1) % 4);
    }, 260);
    return () => clearInterval(interval);
  }, [roaming]);

  const bob = roaming
    ? mood === 'zoomies'
      ? Math.sin(frame * (Math.PI / 2)) * 4
      : Math.sin(frame) * 2
    : 0;
  const icon = getPetEmoji(type);

  const content = (
    <div className="relative">
      {!type.includes('bird') && <div className="h-2 w-8 rounded-full bg-[#92400e]/60" />}
      <div className={`absolute text-lg ${type.includes('bird') ? '-top-2 left-1' : '-top-4 left-1'}`}>{icon}</div>
      {roaming && (
        <div className="absolute -top-6 left-1/2 -translate-x-1/2 rounded-full bg-white/85 px-1.5 py-0.5 text-[8px] shadow">
          {getPetBadge(mood)}
        </div>
      )}
    </div>
  );

  return (
    <Sprite3D x={x} y={y - bob} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={`transition-[left,top] duration-[1600ms] ease-in-out ${scale}`}>
      {content}
    </Sprite3D>
  );
};

PetSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  type: PropTypes.string,
  mood: PropTypes.string,
  roaming: PropTypes.bool,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const LampSprite = ({ x, y, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative">
      <div className="absolute left-1/2 top-0 h-4 w-8 -translate-x-1/2 rounded-t-full bg-[#fef3c7] shadow-[0_0_18px_rgba(253,224,71,0.35)]" />
      <div className="absolute left-1/2 top-4 h-8 w-1 -translate-x-1/2 bg-[#92400e]" />
      <div className="absolute left-1/2 top-12 h-2 w-5 -translate-x-1/2 rounded-full bg-[#92400e]" />
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

LampSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const ArcadeSprite = ({ x, y, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative h-16 w-10 rounded-t-xl bg-[#7c3aed] shadow-lg">
      <div className="absolute left-1 top-1 h-6 w-8 rounded-md bg-[#111827]">
        <div className="mx-auto mt-1 h-4 w-6 rounded-sm bg-gradient-to-b from-[#22c55e] to-[#15803d]" />
      </div>
      <div className="absolute bottom-4 left-4 h-2 w-2 rounded-full bg-[#ef4444]" />
      <div className="absolute bottom-2 left-3 flex gap-1">
        <div className="h-1.5 w-1.5 rounded-full bg-[#fbbf24]" />
        <div className="h-1.5 w-1.5 rounded-full bg-[#3b82f6]" />
      </div>
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

ArcadeSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

export const TableSprite = ({ x, y, width = 88, height = 48, compact = false, isDollhouseView = false }) => {
  const scale = compact ? 'scale-[0.82]' : 'scale-100';

  const content = (
    <div className="relative">
      <div className="rounded-2xl bg-[#7c5b3d] shadow-lg" style={{ width, height: height / 2 }} />
      <div className="absolute top-[80%] left-[15%] h-4 w-2 rounded-sm bg-[#4b2e1a]" />
      <div className="absolute top-[80%] right-[15%] h-4 w-2 rounded-sm bg-[#4b2e1a]" />
    </div>
  );

  return (
    <Sprite3D x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={scale}>
      {content}
    </Sprite3D>
  );
};

TableSprite.propTypes = {
  x: PropTypes.number.isRequired,
  y: PropTypes.number.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  compact: PropTypes.bool,
  isDollhouseView: PropTypes.bool,
};

// ============================================
// SHARED RENDERER
// ============================================

/**
 * Render furniture item based on type
 * @param {Object} item - Furniture item with type, x, y, id
 * @param {number} index - Item index for key generation
 * @param {boolean} compact - Whether to use compact size
 * @param {boolean} isDollhouseView - Whether in 3D dollhouse view
 * @returns {React.Element} Rendered sprite component
 */
export const renderFurniture = (item, index, compact = false, isDollhouseView = false) => {
  const meta = getFurnitureMeta(item.type);
  const x = item.x || 0;
  const y = item.y || 0;
  const id = item.id || `item-${index}`;

  switch (meta.kind) {
    case 'desk':
      return <DeskSprite key={id} x={x} y={y} variant={item.type.includes('gaming') ? 'gaming' : 'modern'} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'chair':
      return <ChairSprite key={id} x={x} y={y} color={item.type.includes('gaming') ? '#7c3aed' : '#475569'} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'sofa':
      return <SofaSprite key={id} x={x} y={y} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'storage':
      return <StorageSprite key={id} x={x} y={y} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'refreshment':
      return <RefreshmentSprite key={id} x={x} y={y} type={item.type} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'server':
      return <ServerSprite key={id} x={x} y={y} active={true} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'plant':
      return <PlantSprite key={id} x={x} y={y} large={item.type.includes('tree')} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'rug':
      return <RugSprite key={id} x={x} y={y} width={item.type.includes('large') ? 132 : 96} height={item.type.includes('large') ? 84 : 60} compact={compact} />;
    case 'wall':
      return <WindowSprite key={id} x={x} y={y} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'divider':
      return <DividerSprite key={id} x={x} y={y} width={item.type.includes('large') ? 130 : 90} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'table':
      return <TableSprite key={id} x={x} y={y} width={meta.width} height={meta.height} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'pet':
      return <PetSprite key={id} x={x} y={y} type={item.type} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'lamp':
      return <LampSprite key={id} x={x} y={y} compact={compact} isDollhouseView={isDollhouseView} />;
    case 'arcade':
      return <ArcadeSprite key={id} x={x} y={y} compact={compact} isDollhouseView={isDollhouseView} />;
    default:
      return (
        <Sprite3D key={id} x={x} y={y} zIndex={Math.floor(y)} isDollhouseView={isDollhouseView} className={compact ? 'scale-[0.82]' : 'scale-100'}>
          <div
            className="flex items-center justify-center rounded-xl border border-white/10 bg-slate-800/70 text-lg shadow-lg"
            style={{ width: meta.width, height: meta.height }}
          >
            {item.emoji || '✨'}
          </div>
        </Sprite3D>
      );
  }
};

export default {
  Sprite3D,
  getFurnitureMeta,
  DeskSprite,
  ChairSprite,
  PlantSprite,
  SofaSprite,
  StorageSprite,
  RefreshmentSprite,
  ServerSprite,
  DividerSprite,
  WindowSprite,
  RugSprite,
  PetSprite,
  LampSprite,
  ArcadeSprite,
  TableSprite,
  renderFurniture,
};
